import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";
import CustomTable from "../CustomTable/CustomTable";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import {
  getSegregatedStrategies,
  getSegregatedTradesByBatch,
  getStrategyBatches,
} from "../../api/strategyProfiling";
import { getBatchTabCache, setBatchTabCache } from "./batchDataCache";
import {
  BATCH_TRADE_COLUMNS,
  sortStrategyBatchesByCreatedAt,
  makeBatchExpandKey,
  normalizeBatchTrades,
  normalizeStrategyBatches,
  normalizeStrategySummaries,
} from "./batchManagementUtils";
import CreateBatchIdModal from "./CreateBatchIdModal";
import styles from "./BatchManagementTab.module.css";

const getErrorMessage = (err) => err?.response?.data?.detail || err.message || "An error occurred";

const formatCurrency = (amount) => {
  const absAmount = Math.abs(amount);
  const parts = absAmount.toFixed(3).split(".");
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `₹ ${integerPart}.${parts[1]}`;
};

const COLUMNS_WITH_DROPDOWN = ["broker_code", "trading_symbol", "exchange"];
const STRATEGY_ICON_COLORS = ["#2DD4BF", "#FB7185", "#60A5FA", "#FBBF24", "#A78BFA", "#F472B6"];
const EMPTY_SELECTION = [];

const BatchTradeTable = memo(function BatchTradeTable({
  trades,
  selectedRows,
  onRowSelect,
  isLoading,
}) {
  if (isLoading) {
    return <div className={styles["loading-message"]}>Loading trades...</div>;
  }

  return (
    <div className={styles["batch-table-wrapper"]}>
      <CustomTable
        columns={BATCH_TRADE_COLUMNS}
        data={trades}
        onRowSelect={onRowSelect}
        selectedRows={selectedRows}
        rowKey="id"
        enableSearch={false}
        enableSorting={true}
        enableSelection={true}
        enableShowEntries={true}
        enableFilter={false}
        enableExport={false}
        enablePagination={true}
        defaultEntriesPerPage={25}
        columnsWithDropdown={COLUMNS_WITH_DROPDOWN}
      />
    </div>
  );
});

const BatchIdRow = memo(function BatchIdRow({
  batchLabel,
  trades,
  isExpanded,
  isLoadingTrades,
  selectedRows,
  onToggle,
  onRowSelect,
}) {
  return (
    <div className={`${styles["batch-id-row"]} ${isExpanded ? styles["batch-id-row-expanded"] : ""}`}>
      <div
        className={styles["batch-id-row-content"]}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <span className={styles["batch-id-label"]} title={batchLabel}>
          {batchLabel}
        </span>
        <span className={styles["batch-expand-button"]} aria-hidden="true">
          <GlobalIcon
            iconName="ChevronDownIcon"
            className={`${styles["batch-chevron-icon"]} ${isExpanded ? styles["expanded"] : ""}`}
          />
        </span>
      </div>

      {isExpanded ? (
        <div className={`${styles["batch-id-expanded-content"]} ${styles["expanded"]}`}>
          <BatchTradeTable
            trades={trades}
            selectedRows={selectedRows}
            onRowSelect={onRowSelect}
            isLoading={isLoadingTrades}
          />
        </div>
      ) : null}
    </div>
  );
});

const BatchStrategyCard = memo(function BatchStrategyCard({
  strategy,
  iconColor,
  isExpanded,
  batches,
  isLoadingBatches,
  batchesError,
  expandedBatches,
  batchTrades,
  loadingBatchTrades,
  selectedBatchTrades,
  onToggleStrategy,
  onToggleBatch,
  onBatchRowSelect,
}) {
  const isProfit = (strategy.pnl || 0) >= 0;
  const sortedBatches = useMemo(
    () => sortStrategyBatchesByCreatedAt(batches),
    [batches]
  );

  return (
    <div
      className={`${styles["batch-strategy-row"]} ${
        isExpanded ? styles["batch-strategy-row-expanded"] : ""
      }`}
    >
      <div
        className={styles["batch-strategy-row-content"]}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={onToggleStrategy}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggleStrategy();
          }
        }}
      >
        <div className={styles["batch-strategy-identity"]}>
          <span
            className={styles["batch-strategy-icon-dot"]}
            style={{ backgroundColor: iconColor }}
            aria-hidden
          />
          <div className={styles["batch-strategy-name"]} title={strategy.strategyName}>
            {strategy.strategyName}
          </div>
        </div>

        <div className={styles["batch-strategy-pnl-block"]}>
          <div className={styles["batch-strategy-pnl-indicator"]}>
            {isProfit ? (
              <GlobalIcon iconName="ProfitArrowIcon" className={styles["batch-strategy-pnl-arrow"]} />
            ) : (
              <GlobalIcon iconName="LossArrowIcon" className={styles["batch-strategy-pnl-arrow"]} />
            )}
            <span
              className={`${styles["batch-strategy-pnl-label"]} ${
                isProfit ? styles["profit"] : styles["loss"]
              }`}
            >
              Profit &amp; Loss
            </span>
          </div>
          <span className={styles["batch-strategy-pnl-amount"]}>
            {formatCurrency(strategy.pnl || 0)}
          </span>
        </div>

        <div className={styles["batch-strategy-stat"]}>
          <span className={styles["batch-strategy-stat-label"]}>Total Trades-</span>
          <span className={styles["batch-strategy-stat-value"]}>
            {(strategy.tradeCount || 0).toLocaleString("en-IN")}
          </span>
        </div>

        <div className={`${styles["batch-strategy-stat"]} ${styles["batch-strategy-stat-accent"]}`}>
          <span className={styles["batch-strategy-stat-label"]}>Total Batch ID-</span>
          <span className={styles["batch-strategy-stat-value"]}>{strategy.batchCount || 0}</span>
        </div>

        <span className={styles["batch-expand-button"]} aria-hidden="true">
          <GlobalIcon
            iconName="ChevronDownIcon"
            className={`${styles["batch-chevron-icon"]} ${isExpanded ? styles["expanded"] : ""}`}
          />
        </span>
      </div>

      {isExpanded ? (
        <div className={`${styles["batch-strategy-expanded-content"]} ${styles["expanded"]}`}>
          {batchesError ? <div className={styles["error-message"]}>{batchesError}</div> : null}
          {isLoadingBatches ? (
            <div className={styles["loading-message"]}>Loading batches...</div>
          ) : sortedBatches.length === 0 ? (
            <div className={styles["batch-empty-message"]}>No batches found for this strategy</div>
          ) : (
            sortedBatches.map((batch) => {
              const batchKey = makeBatchExpandKey(strategy.strategyId, batch.id);
              const isBatchExpanded = expandedBatches.has(batchKey);

              return (
                <BatchIdRow
                  key={batchKey}
                  batchLabel={batch.description}
                  trades={batchTrades[batchKey] || EMPTY_SELECTION}
                  isExpanded={isBatchExpanded}
                  isLoadingTrades={Boolean(loadingBatchTrades[batchKey])}
                  selectedRows={selectedBatchTrades[batchKey] || EMPTY_SELECTION}
                  onToggle={() => onToggleBatch(strategy.strategyId, batch.id)}
                  onRowSelect={(selectedRowIds) => onBatchRowSelect(batchKey, selectedRowIds)}
                />
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
});

function BatchManagementTab() {
  const cachedData = getBatchTabCache();
  const [strategies, setStrategies] = useState(cachedData?.strategies || []);
  const [strategyBatches, setStrategyBatches] = useState(cachedData?.strategyBatches || {});
  const [batchTrades, setBatchTrades] = useState(cachedData?.batchTrades || {});
  const [loadingStrategyBatches, setLoadingStrategyBatches] = useState({});
  const [loadingBatchTrades, setLoadingBatchTrades] = useState({});
  const [strategyBatchesErrors, setStrategyBatchesErrors] = useState({});
  const [expandedStrategies, setExpandedStrategies] = useState(() => new Set());
  const [expandedBatches, setExpandedBatches] = useState(() => new Set());
  const [selectedBatchTrades, setSelectedBatchTrades] = useState({});
  const [isLoading, setIsLoading] = useState(!cachedData);
  const [error, setError] = useState("");
  const [isCreateBatchModalOpen, setIsCreateBatchModalOpen] = useState(false);
  const strategyBatchesRef = useRef(strategyBatches);
  const batchTradesRef = useRef(batchTrades);
  const strategiesRef = useRef(strategies);

  strategyBatchesRef.current = strategyBatches;
  batchTradesRef.current = batchTrades;
  strategiesRef.current = strategies;

  const persistCache = useCallback((partial) => {
    setBatchTabCache({
      strategies: partial.strategies ?? strategiesRef.current,
      strategyBatches: partial.strategyBatches ?? strategyBatchesRef.current,
      batchTrades: partial.batchTrades ?? batchTradesRef.current,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadInitialData = async () => {
      const existingCache = getBatchTabCache();
      if (existingCache?.strategies?.length) {
        setStrategies(existingCache.strategies);
        setStrategyBatches(existingCache.strategyBatches || {});
        setBatchTrades(existingCache.batchTrades || {});
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      try {
        const strategiesResponse = await getSegregatedStrategies();
        if (cancelled) return;

        const strategiesData = normalizeStrategySummaries(strategiesResponse?.data || []);

        setStrategies(strategiesData);
        persistCache({ strategies: strategiesData });
      } catch (err) {
        if (!cancelled) {
          setError(getErrorMessage(err));
          setStrategies([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [persistCache]);

  const fetchStrategyBatches = useCallback(async (strategyId, { force = false } = {}) => {
    if (!force && strategyBatchesRef.current[strategyId]) return;

    setLoadingStrategyBatches((prev) => ({ ...prev, [strategyId]: true }));
    setStrategyBatchesErrors((prev) => {
      const next = { ...prev };
      delete next[strategyId];
      return next;
    });

    try {
      const response = await getStrategyBatches(strategyId);
      const batches = normalizeStrategyBatches(Array.isArray(response) ? response : response?.data || []);
      setStrategyBatches((prev) => {
        const next = { ...prev, [strategyId]: batches };
        persistCache({ strategyBatches: next });
        return next;
      });
    } catch (err) {
      setStrategyBatchesErrors((prev) => ({
        ...prev,
        [strategyId]: getErrorMessage(err),
      }));
    } finally {
      setLoadingStrategyBatches((prev) => ({ ...prev, [strategyId]: false }));
    }
  }, [persistCache]);

  const fetchBatchTrades = useCallback(async (strategyId, batchId) => {
    const batchKey = makeBatchExpandKey(strategyId, batchId);
    if (batchTradesRef.current[batchKey]) return;

    setLoadingBatchTrades((prev) => ({ ...prev, [batchKey]: true }));

    try {
      const response = await getSegregatedTradesByBatch(batchId);
      const trades = normalizeBatchTrades(response?.data || response?.trades || response || []);
      setBatchTrades((prev) => {
        const next = { ...prev, [batchKey]: trades };
        persistCache({ batchTrades: next });
        return next;
      });
    } catch (err) {
      setBatchTrades((prev) => ({ ...prev, [batchKey]: [] }));
      setStrategyBatchesErrors((prev) => ({
        ...prev,
        [batchKey]: getErrorMessage(err),
      }));
    } finally {
      setLoadingBatchTrades((prev) => ({ ...prev, [batchKey]: false }));
    }
  }, [persistCache]);

  const toggleStrategyExpand = useCallback((strategyId) => {
    setExpandedStrategies((prev) => {
      const next = new Set(prev);
      const willExpand = !next.has(strategyId);
      if (willExpand) {
        next.add(strategyId);
        fetchStrategyBatches(strategyId);
      } else {
        next.delete(strategyId);
      }
      return next;
    });
  }, [fetchStrategyBatches]);

  const toggleBatchExpand = useCallback((strategyId, batchId) => {
    const key = makeBatchExpandKey(strategyId, batchId);
    setExpandedBatches((prev) => {
      const next = new Set(prev);
      const willExpand = !next.has(key);
      if (willExpand) {
        next.add(key);
        fetchBatchTrades(strategyId, batchId);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, [fetchBatchTrades]);

  const handleCreateBatchId = useCallback(() => {
    setIsCreateBatchModalOpen(true);
  }, []);

  const handleCloseCreateBatchModal = useCallback(() => {
    setIsCreateBatchModalOpen(false);
  }, []);

  const handleCreateBatchSuccess = useCallback(
    async (result) => {
      const strategyId = result?.strategyId;
      if (!strategyId) return;

      setStrategyBatches((prev) => {
        const next = { ...prev };
        delete next[strategyId];
        persistCache({ strategyBatches: next });
        return next;
      });

      try {
        const strategiesResponse = await getSegregatedStrategies();
        const strategiesData = normalizeStrategySummaries(strategiesResponse?.data || []);
        setStrategies(strategiesData);
        persistCache({ strategies: strategiesData });
      } catch (err) {
        setError(getErrorMessage(err));
      }

      setExpandedStrategies((expanded) => {
        if (expanded.has(strategyId)) {
          fetchStrategyBatches(strategyId, { force: true });
        }
        return expanded;
      });
    },
    [fetchStrategyBatches, persistCache]
  );

  const handleBatchRowSelect = useCallback((batchKey, selectedRowIds) => {
    setSelectedBatchTrades((prev) => ({
      ...prev,
      [batchKey]: selectedRowIds,
    }));
  }, []);

  return (
    <div className={styles["batch-management-content"]}>
      <div className={styles["batch-management-header"]}>
        <h2 className={styles["batch-management-title"]}>All Strategies</h2>
        <Button
          variant="contained"
          className={styles["create-batch-button"]}
          onClick={handleCreateBatchId}
        >
          + Create Batch ID
        </Button>
      </div>

      {error && <div className={styles["error-message"]}>{error}</div>}

      {isLoading && strategies.length === 0 ? (
        <div className={styles["loading-message"]}>Loading batch management...</div>
      ) : (
        <div className={styles["batch-strategies-list"]}>
          {strategies.length === 0 ? (
            <div className={styles["loading-message"]}>No strategies found</div>
          ) : (
            strategies.map((strategy, index) => (
              <BatchStrategyCard
                key={strategy.strategyId}
                strategy={strategy}
                iconColor={STRATEGY_ICON_COLORS[index % STRATEGY_ICON_COLORS.length]}
                isExpanded={expandedStrategies.has(strategy.strategyId)}
                batches={strategyBatches[strategy.strategyId] || []}
                isLoadingBatches={Boolean(loadingStrategyBatches[strategy.strategyId])}
                batchesError={strategyBatchesErrors[strategy.strategyId]}
                expandedBatches={expandedBatches}
                batchTrades={batchTrades}
                loadingBatchTrades={loadingBatchTrades}
                selectedBatchTrades={selectedBatchTrades}
                onToggleStrategy={() => toggleStrategyExpand(strategy.strategyId)}
                onToggleBatch={toggleBatchExpand}
                onBatchRowSelect={handleBatchRowSelect}
              />
            ))
          )}
        </div>
      )}
      <CreateBatchIdModal
        open={isCreateBatchModalOpen}
        onClose={handleCloseCreateBatchModal}
        onSuccess={handleCreateBatchSuccess}
      />
    </div>
  );
}

export default memo(BatchManagementTab);
